import { FunctionalComponent } from 'preact';
import { useEffect, useState, useRef } from 'preact/hooks';
import { useDispatch, useSelector } from 'react-redux';
import { Suspense, lazy } from 'preact/compat';

import {
  removeTile,
  editTile,
  addShortcut,
  selectAdTiles,
  selectShortcuts,
  selectPagedTiles,
} from './slice';

import { fetchTiles } from './slice/thunks';

import { AdTile as AdTileType, Tile as TileType } from './slice/types';

import { selectOnboardingStep } from '../user/slice';

import UrlTile from './components/UrlTile';
import AdTile from './components/AdTile';
import PlaceholderTile from './components/PlaceholderTile';
import AddTileShortcut from './components/AddTileShortcut';
import PaginationButton from './components/PaginationButton';

import styles from './styles.module.scss';
import GameTile from './components/GameTile';
import { selectUiMode } from '../../store/selectors';

const RemoveGameTileDialog = lazy(
  () => import('./components/GameTile/RemoveDialog')
);
const LinkDialog = lazy(() => import('./components/LinkDialog'));
const RemoveAdTileDialog = lazy(
  () => import('./components/RemoveAdTileDialog')
);

const MAX_PAGE = 3;

const DIALOGS = {
  REMOVE_GAME: RemoveGameTileDialog,
  REMOVE_AD: RemoveAdTileDialog,
  EDIT_TILE: LinkDialog,
};

const shouldTilesSlideDefault = { slide: false, direction: '' };

const Tiles: FunctionalComponent = () => {
  const dispatch = useDispatch();
  const uiMode = useSelector(selectUiMode);

  const adTiles = useSelector(selectAdTiles);
  const adTilesRef = useRef(null);

  useEffect(() => {
    adTilesRef.current = adTiles;
  }, [adTiles]);

  const shortcutsTiles = useSelector(selectShortcuts);
  const onboardingStep = useSelector(selectOnboardingStep);
  const pagedTiles = useSelector(selectPagedTiles);

  const shortcutsTilesRef = useRef(shortcutsTiles);
  const [newTile, setNewTile] = useState(null);

  useEffect(() => {
    if (shortcutsTiles.length > shortcutsTilesRef.current.length) {
      const found = shortcutsTiles.find(
        ({ id }) => !shortcutsTilesRef.current.find((tile) => id === tile.id)
      );
      setNewTile(found);
    }

    shortcutsTilesRef.current = shortcutsTiles;
  }, [shortcutsTiles]);

  const [selectedTile, setSelectedTile] = useState(null);

  const [page, setPage] = useState(-1);

  const [shouldTilesSlide, setShouldTilesSlide] = useState(
    shouldTilesSlideDefault
  );
  const [showTileDialog, setShowTileDialog] = useState(null);

  const closeDialog = (): void => setShowTileDialog(null);

  const handleRemoveTile = (): void => {
    if (selectedTile.type === 'adTiles' && showTileDialog !== 'REMOVE_AD') {
      setShowTileDialog('REMOVE_AD');
      return;
    }

    const index = pagedTiles[page].filter(Boolean).indexOf(selectedTile);
    dispatch(removeTile({ tile: selectedTile, index }));
    closeDialog();
  };

  const openDialog = (item = null): void => {
    const tileItem = item || { type: 'shortcuts' };
    setSelectedTile(tileItem);

    setShowTileDialog(tileItem.type === 'games' ? 'REMOVE_GAME' : 'EDIT_TILE');
  };

  const saveDialog = (data): void => {
    const dialogData = {
      ...data,
      url: !/^https?:\/\//i.test(data.url) ? `https://${data.url}` : data.url,
    };
    closeDialog();
    dispatch((dialogData.id ? editTile : addShortcut)(dialogData));
    if (!dialogData.id) setPage(0);
  };

  useEffect(() => {
    setPage(0);
    dispatch(fetchTiles());
  }, []);

  useEffect(() => {
    setTimeout(() => setShouldTilesSlide(shouldTilesSlide), 100);
  }, [shouldTilesSlide.direction]);

  const handlePagination = (direction: string): void => {
    const totalPage = pagedTiles.length - 1;

    setPage((v) =>
      direction === 'forward'
        ? v === totalPage
          ? 0
          : v + 1
        : v === 0
          ? totalPage
          : v - 1
    );

    setShouldTilesSlide({ slide: true, direction });
  };

  const RemoveDialogComponent = DIALOGS[showTileDialog];
  const tilesRef = useRef<HTMLDivElement>(null);
  const timeout = useRef<NodeJS.Timeout>(null);
  const isNavigatedRecently = useRef(false);

  useEffect(() => {
    const handleArrowKeys = (event): void => {
      if (isNavigatedRecently.current) return;

      if (event.key === 'ArrowRight') {
        isNavigatedRecently.current = true;
        handlePagination('forward');

        timeout.current = setTimeout(() => {
          isNavigatedRecently.current = false;
        }, 100);
      } else if (event.key === 'ArrowLeft') {
        isNavigatedRecently.current = true;
        handlePagination('backward');

        timeout.current = setTimeout(() => {
          isNavigatedRecently.current = false;
        }, 100);
      }
    };

    const handleWheel = (event): void => {
      event.preventDefault();

      if (isNavigatedRecently.current) {
        return;
      }

      if (event.deltaY > 10 || event.deltaX > 10) {
        isNavigatedRecently.current = true;
        handlePagination('forward');
        timeout.current = setTimeout(() => {
          isNavigatedRecently.current = false;
        }, 500);
      } else if (event.deltaY < -10 || event.deltaX < -10) {
        isNavigatedRecently.current = true;
        handlePagination('backward');

        timeout.current = setTimeout(() => {
          isNavigatedRecently.current = false;
        }, 500);
      }
    };

    document.addEventListener('keydown', handleArrowKeys);
    tilesRef.current.addEventListener('wheel', handleWheel);

    return (): void => {
      document.body.removeEventListener('keydown', handleArrowKeys);
      tilesRef.current.removeEventListener('wheel', handleWheel);
    };
  }, []);

  return (
    <div
      className={`${styles.mostVisited} ${shouldTilesSlide.slide
        ? `${styles.animate} ${styles[shouldTilesSlide.direction]}`
        : ''
        } mostVisitedTC`}
      ref={tilesRef}
    >
      {page >= 1 && (
        <PaginationButton direction="backward" onClick={handlePagination} />
      )}

      {page === -1 &&
        Array(process.env.APP_PLATFORM === 'web' ? 4 : 10)
          .fill('')
          .map((v, i) => <PlaceholderTile key={i} />)}

      {pagedTiles[page]?.filter(Boolean).map((tile: AdTileType | TileType, index) => {
        const params = {
          key: tile.url || tile.id,
          item: tile,
          onClickMore: (): void => openDialog(tile),
        };

        if (tile.type === 'adTiles') {
          return (
            <>
              {(tile as AdTileType).impression_url && (
                <img
                  src={(tile as AdTileType).impression_url}
                  className={styles.adImpression}
                  alt=""
                />
              )}
              <AdTile
                {...params}
                index={index}
                item={tile as AdTileType}
                isGlowing={onboardingStep === 'topSites'}
              />
            </>
          );
        } else if (tile.type === 'games') {
          return uiMode === 'focus' ? (
            <PlaceholderTile />
          ) : (
            <GameTile {...params} />
          );
        }

        // eslint-disable-next-line react/jsx-key
        return <UrlTile {...params} isNew={tile === newTile} />;
      })}

      {pagedTiles[page + 1] && page !== MAX_PAGE && (
        <PaginationButton direction="forward" onClick={handlePagination} />
      )}

      <AddTileShortcut
        onClick={(): void => openDialog()}
        noTiles={!pagedTiles[page]}
      />

      {['REMOVE_GAME', 'REMOVE_AD', 'EDIT_TILE'].includes(showTileDialog) && (
        <Suspense fallback={null}>
          <RemoveDialogComponent
            initData={showTileDialog === 'EDIT_TILE' && selectedTile}
            onSubmit={showTileDialog === 'EDIT_TILE' && saveDialog}
            onClose={closeDialog}
            onRemove={handleRemoveTile}
          />
        </Suspense>
      )}
    </div>
  );
};

export default Tiles;
